import { useParams } from "@tanstack/react-router";
import { useDeliverApi } from "@bfc-software/bfc-web-ui/lib/deliver-api-client";

/**
 * The warehouse the Manage screens are working on, resolved from the
 * `$tenant` URL segment against the tenants the caller may see.
 *
 * `orgId` and `warehouseCode` are what AAA's `/app_user/*` calls are keyed on,
 * so the screens take them from here rather than from the route's `$org` slug:
 * a `bfc_deliver_admin` sees tenants across organisations, and a tenant's own
 * org is the only safe scope for writes to it.
 *
 * `status` separates "still loading" from "not one of yours", which the shell
 * needs to tell apart - the second goes back to the dashboard.
 */
export function useManageTenant() {
  const { tenant: tenantParam } = useParams({ strict: false });
  const client = useDeliverApi();
  const tenants = client.useQuery(
    "get",
    "/app/deliver/api/tenants",
    undefined,
    {
      staleTime: 5 * 60 * 1000,
    },
  );

  const tenant = tenants.data?.rows.find(
    (row) => String(row.tenantId) === tenantParam,
  );

  const status = tenants.isError
    ? "error"
    : tenants.isLoading
      ? "loading"
      : tenant
        ? "ready"
        : "unknown";

  return { tenant, status } as const;
}
